import React, { useState } from 'react';
import { Flag, X, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../api';
import './ReportModal.css';

const PREDEFINED_REASONS = [
    { id: 'Harassment or Bullying', label: 'Harassment or Bullying', icon: '🛑' },
    { id: 'Hate Speech', label: 'Hate Speech', icon: '⚠️' },
    { id: 'Spam', label: 'Spam', icon: '🚫' },
    { id: 'False Information', label: 'False Information', icon: '❌' },
    { id: 'Inappropriate Content', label: 'Inappropriate Content', icon: '🔞' },
    { id: 'Violence or Threats', label: 'Violence or Threats', icon: '⚔️' },
    { id: 'Other', label: 'Other', icon: '📝' }
];

const ReportModal = ({ isOpen, onClose, contentType = 'post', contentId, onSuccess }) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [submittedSuccess, setSubmittedSuccess] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setSelectedReason('');
        setCustomReason('');
        setError('');
        setSubmittedSuccess(false);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedReason) {
            setError('Please select a reason for reporting.');
            return;
        }
        if (selectedReason === 'Other' && !customReason.trim()) {
            setError('Please describe the reason in your own words.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            await api.post('/discussions/report', {
                content_type: contentType,
                content_id: contentId,
                reason: selectedReason,
                custom_reason: selectedReason === 'Other' ? customReason.trim() : null
            });

            setSubmittedSuccess(true);
            setTimeout(() => {
                handleClose();
                if (onSuccess) onSuccess();
            }, 1800);
        } catch (err) {
            console.error('Failed to submit report:', err);
            setError(err.response?.data?.detail || 'Failed to submit report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="report-modal-overlay" onClick={handleClose}>
            <div className="report-modal-box glass-card" onClick={(e) => e.stopPropagation()}>
                <div className="report-modal-header">
                    <div className="report-modal-title">
                        <div className="report-icon-pill">
                            <Flag size={18} />
                        </div>
                        <div>
                            <h3>Report Content</h3>
                            <p className="report-subtitle">Help us understand what's wrong with this {contentType}</p>
                        </div>
                    </div>
                    <button className="report-close-btn" onClick={handleClose} type="button">
                        <X size={18} />
                    </button>
                </div>

                {submittedSuccess ? (
                    <div className="report-success-state">
                        <CheckCircle size={48} className="success-icon" />
                        <h4>Report Submitted</h4>
                        <p>Thank you for bringing this to our attention. Our moderation team will review this content shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="report-modal-body">
                        {error && (
                            <div className="report-error-banner">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <label className="report-section-label">Select a reason for reporting:</label>
                        <div className="report-reasons-list">
                            {PREDEFINED_REASONS.map((item) => (
                                <label
                                    key={item.id}
                                    className={`report-reason-option ${selectedReason === item.id ? 'selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="reportReason"
                                        value={item.id}
                                        checked={selectedReason === item.id}
                                        onChange={() => {
                                            setSelectedReason(item.id);
                                            setError('');
                                        }}
                                    />
                                    <span className="reason-emoji">{item.icon}</span>
                                    <span className="reason-label">{item.label}</span>
                                </label>
                            ))}
                        </div>

                        {selectedReason === 'Other' && (
                            <div className="report-custom-area">
                                <label className="report-section-label">Describe the issue:</label>
                                <textarea
                                    className="report-textarea"
                                    placeholder="Please provide details about why this content is inappropriate..."
                                    value={customReason}
                                    onChange={(e) => {
                                        setCustomReason(e.target.value);
                                        setError('');
                                    }}
                                    rows={4}
                                    maxLength={500}
                                />
                                <div className="report-char-count">{customReason.length}/500</div>
                            </div>
                        )}

                        <div className="report-modal-footer">
                            <button type="button" className="report-cancel-btn" onClick={handleClose} disabled={submitting}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="report-submit-btn"
                                disabled={submitting || !selectedReason || (selectedReason === 'Other' && !customReason.trim())}
                            >
                                {submitting ? 'Submitting...' : 'Submit Report'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ReportModal;
